// Phase-A R5 — Redesigned LoginPage.
// Visual design from docs/my-upload/login/LoginPage.tsx (owner template).
// Wired to the existing auth flow (auth.login, DEMO_CREDS, apiRoleToLocal,
// role-aware redirect, error toasts) — no behaviour regression vs the
// pre-R5 LoginPage in Auth.tsx.
//
// Demo quick-login by role: an 11-chip grid (all roles) shown only on the
// demo tenant. Clicking a chip fills that role's real seeded demo account, so
// login actually lands in that role's workspace — functional, not cosmetic.
// The post-login role still comes from the authenticated account server-side
// (server `roles` → apiRoleToLocal → redirect), never from the chip. The rest
// of the R5 login layout/theme is unchanged.
import React from "react";
import { useTranslation, Trans } from "react-i18next";
import { useRole, ROLES, type RoleId } from "../../role";
import { apiRoleToLocal } from "../../auth/role-map";
import { useAuth, isTwoFactorChallenge } from "../../auth/AuthContext";
import { ApiError } from "../../api/client.js";
import { formatLockCountdown, isLockActive, lockRemainingSeconds } from "../../auth/lockout-countdown";
import { toEnDigits } from "../../i18n/jalali";
import { useLocale } from "../../i18n/LocaleProvider";
import { localeMetadata } from "../../i18n/locale-registry";
import type { Go } from "../../router";
import { BrandPanel } from "./BrandPanel";
import { CoBrandFooter } from "./CoBrandFooter";
import {
  Icon,
  Field,
  TextInput,
  Checkbox,
  Eyebrow,
  PillButton,
  Wordmark,
  Spinner,
  PassStrength,
  SsoButton,
  GoogleGlyph,
  NationalGlyph,
  OrgGlyph,
  useR5Toast,
} from "./login-atoms";

// ---- Demo accounts (one per role) ----------------------------------------
// The role chips are a DEMO QUICK-LOGIN, NOT an auth constraint: the
// post-login role always comes from the authenticated account (server
// `roles` → apiRoleToLocal). Each chip fills that role's real seeded account
// EMAIL only — the password is deliberately NOT shipped to the client (it would
// otherwise be readable in the public bundle, defeating the point of rotating
// it). The operator types the secret (held only on the server / by the owner).
// Shown only on the demo tenant (that's where these accounts live).

const EMAIL_PLACEHOLDER = "you@example.com";

interface DemoAccount {
  roleId: RoleId;
  email: string;
}

// S-1 (MASTER_SPEC ۳.۱) — the quick-login demo widget is a BUILD-TIME opt-in.
// `VITE_DEMO_MODE` is a Vite env replaced at build time; the docker-compose
// build-arg default is "false" (S-UX-WAVE1), so when the VPS .env does not
// explicitly override it, `DEMO_MODE` folds to a compile-time `false` and the
// DEMO_ACCOUNTS array below is the dead branch of a constant ternary — TREE-
// SHAKEN out of the bundle entirely, the role emails never ship. Production
// currently sets `VITE_DEMO_MODE=true` explicitly (via `remote.ps1 set-env`)
// to keep the existing chip-grid behavior live; a local `.env` can do the same
// for development.
const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === "true";

// Ordered by hierarchy for the chip grid: academic actors → leadership → staff/ops
// → oversight → external. Every email is a real seeded account on the demo tenant.
// Display labels for each role live in the `identity` i18n namespace under
// `login.demoRoles.<roleId>` (resolved at render time in DemoRolePicker /
// pickDemo below) — this array is module-scope and evaluated before i18n
// boots, so it cannot call t() itself.
const DEMO_ACCOUNTS: DemoAccount[] = DEMO_MODE
  ? [
      // — academic —
      { roleId: "student", email: "student1@digiuniversity.ir" },
      { roleId: "instructor", email: "instructor1@digiuniversity.ir" },
      { roleId: "ta", email: "ta1@digiuniversity.ir" },
      // — leadership —
      { roleId: "faculty_admin", email: "facadmin1@digiuniversity.ir" },
      { roleId: "admin", email: "admin@digiuniversity.ir" },
      { roleId: "super_admin", email: "superadmin@digiuniversity.ir" },
      // — staff / ops —
      { roleId: "content_manager", email: "cm1@digiuniversity.ir" },
      // R-COMPLY — مدیر مالی owns the finance workspace (شهریه/وام/بورس/استرداد/خزانه/حقوق);
      // ریاست دانشگاه (admin) intentionally does NOT.
      { roleId: "finance_manager", email: "finance1@digiuniversity.ir" },
      { roleId: "support", email: "support1@digiuniversity.ir" },
      // S-CARD W0-cfg-b — میزِ عضویت (دو نقشِ تازه، تنظیماتِ عضویت).
      { roleId: "membership_operator", email: "membership1@digiuniversity.ir" },
      { roleId: "membership_manager", email: "membermgr1@digiuniversity.ir" },
      // S-ASSESS-W3a — کنسولِ کانونِ سنجش (ارزیاب مستقل + مدیرِ کانون).
      { roleId: "assessor", email: "assessor1@digiuniversity.ir" },
      { roleId: "assessment_manager", email: "assessmgr1@digiuniversity.ir" },
      // — oversight —
      { roleId: "moderator", email: "moderator1@digiuniversity.ir" },
      // WELL-4 — مشاور دانشگاه (least-privilege wellness desk).
      { roleId: "counselor", email: "counselor1@digiuniversity.ir" },
      // — external —
      { roleId: "parent", email: "parent1@digiuniversity.ir" },
      { roleId: "org", email: "org1@digiuniversity.ir" },
    ]
  : [];

// ---- Demo role quick-login (chip grid) -----------------------------------

interface DemoRolePickerProps {
  selected: RoleId | null;
  onPick: (acct: DemoAccount) => void;
}

const DemoRolePicker: React.FC<DemoRolePickerProps> = ({ selected, onPick }) => {
  const { t } = useTranslation("identity");
  return (
    <div
      style={{
        display: "grid",
        gap: 8,
        padding: 14,
        background: "var(--r5-bg-2)",
        border: "1px dashed var(--r5-line-strong)",
        borderRadius: 14,
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: "var(--r5-muted)",
          fontFamily: "var(--r5-mono)",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
        }}
      >
        {t("login.demoPicker.title")}
      </div>
      <div role="group" aria-label={t("login.demoPicker.groupAriaLabel")} style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {DEMO_ACCOUNTS.map((a) => {
          const active = a.roleId === selected;
          return (
            <button
              key={a.roleId}
              type="button"
              aria-pressed={active}
              onClick={() => onPick(a)}
              style={{
                padding: "7px 13px",
                borderRadius: 999,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                background: active ? "var(--r5-sage-deep)" : "var(--r5-paper)",
                color: active ? "#fff" : "var(--r5-ink-2)",
                border: "1px solid " + (active ? "var(--r5-sage-deep)" : "var(--r5-line-strong)"),
                transition: "all 160ms ease",
              }}
            >
              {t(`login.demoRoles.${a.roleId}`)}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: "var(--r5-muted)", lineHeight: 1.7 }}>
        {t("login.demoPicker.hint")}
      </div>
    </div>
  );
};

// ---- Main page ------------------------------------------------------------

interface AuthPageProps { go: Go }

interface LoginErrors {
  tenantSlug?: string;
  email?: string;
  password?: string;
  general?: string;
}

// R7.9 — apiRoleToLocal moved to apps/web/src/auth/role-map.ts so
// LoginPage and the legacy Auth.tsx share a single source of truth.
// The previous local copy mapped only 3 of 10 API roles, silently
// bucketing parent/org/ta/cm/support/moderator/super_admin as
// "student" — that bug caused Gate A §5 + OWNER-FINDING-2.

export const LoginPage: React.FC<AuthPageProps> = ({ go }) => {
  const { t } = useTranslation(["identity", "profile"]);
  const { locale } = useLocale();
  const language = localeMetadata(locale);
  // Canonical protocol code, not locale-sensitive display-name casing.
  const languageCode = (language?.baseLanguage ?? locale).toUpperCase();
  const languageLabel = t("profile:settings.language.interfaceLangRowLabel");
  const { setRole } = useRole();
  const auth = useAuth();
  const [tenantSlug, setTenantSlug] = React.useState<string>("demo");
  const [email, setEmail] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");
  const [showPass, setShowPass] = React.useState<boolean>(false);
  const [remember, setRemember] = React.useState<boolean>(true);
  const [twofa, setTwofa] = React.useState<boolean>(true);
  const [errors, setErrors] = React.useState<LoginErrors>({});
  const [pending, setPending] = React.useState<boolean>(false);
  // R-AUTH-5 — 2-step login: when the account has 2FA, step 1 returns a pending
  // token and we switch to the code step instead of going home.
  const [twoFaStage, setTwoFaStage] = React.useState<boolean>(false);
  const [pendingToken, setPendingToken] = React.useState<string>("");
  const [twoFaCode, setTwoFaCode] = React.useState<string>("");
  // Demo quick-login: which role chip is currently filled (highlight only).
  const [selectedRole, setSelectedRole] = React.useState<RoleId | null>(null);
  // S-UX-WAVE1 — ledger §۳.۲ ردیفِ ۵: «شناسهٔ سازمان» به‌عنوانِ اولین فیلدِ فرم،
  // کاربرِ ناآشنا را با یک ورودیِ خام مواجه می‌کرد که باید حدس می‌زد «demo» است.
  // در عمل تک‌تک مستأجرهایِ عمومیِ برنامه از راهِ ثابت‌هایِ کد (Apply.tsx,
  // Auth.tsx forgot-password) هم‌همین «demo» را بی‌هیچ ورودیِ کاربر مصرف می‌کنند؛
  // اینجا هم پیش‌فرض «demo» ساکت می‌ماند و فیلد فقط با کلیکِ آگاهانه باز می‌شود —
  // امکانِ ورودِ چندمستأجریِ آینده حذف نشد، فقط از مسیرِ پیش‌فرض کنار رفت.
  const [showTenantField, setShowTenantField] = React.useState(false);
  const { node: toastNode, show: showToast } = useR5Toast();

  // S-2/S-3 wave 2 — per-account lockout countdown. When login returns 429
  // `account_locked`, hold the lock-until timestamp so the submit button disables
  // and a persistent countdown replaces the toast (which would vanish). Ticks
  // every second, self-clears when the window elapses, and is reset when the user
  // edits the account fields (switching account). Inert unless the backend flag is
  // on (it never emits account_locked otherwise) → zero effect on today's flow.
  const [lockUntil, setLockUntil] = React.useState<number | null>(null);
  const [nowTs, setNowTs] = React.useState<number>(() => Date.now());
  React.useEffect(() => {
    if (lockUntil == null) return;
    const id = window.setInterval(() => setNowTs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [lockUntil]);
  const lockRemaining = lockRemainingSeconds(lockUntil, nowTs);
  const isLocked = isLockActive(lockUntil, nowTs);
  React.useEffect(() => {
    if (lockUntil != null && lockRemaining <= 0) setLockUntil(null);
  }, [lockUntil, lockRemaining]);

  const finishLogin = (roles: string[]): void => {
    const localRole = apiRoleToLocal(roles);
    setRole(localRole);
    showToast(t("login.toast.loginSuccess"));
    // S-9 — if the user came here from a plan CTA (guest/logged-out), resume the
    // checkout they picked instead of dumping them on their role home page.
    let pendingPlan: string | null = null;
    try {
      pendingPlan = sessionStorage.getItem("s9_checkout_plan");
      if (pendingPlan) sessionStorage.removeItem("s9_checkout_plan");
    } catch {
      /* sessionStorage unavailable */
    }
    if (pendingPlan) {
      go("checkout", pendingPlan);
      return;
    }
    go(ROLES[localRole]?.homeRoute || "dashboard");
  };

  const pickDemo = (acct: DemoAccount): void => {
    // Fill the EMAIL only — the password is NEVER in the client bundle/seed. Each
    // demo role now has its OWN password, so clear the field on a role switch:
    // the operator always types the password for the role they just picked.
    setEmail(acct.email);
    setPassword("");
    setSelectedRole(acct.roleId);
    // A programmatic setEmail doesn't fire the input's onChange, so clear the lock
    // countdown here too — otherwise a stale lock on the PREVIOUS account would keep
    // submit disabled after switching to a different (unlocked) demo role.
    setLockUntil(null);
    const roleLabel = t(`login.demoRoles.${acct.roleId}`);
    showToast(t("login.toast.demoFilled", { role: roleLabel }));
  };

  const validate = (): LoginErrors => {
    const e: LoginErrors = {};
    if (!tenantSlug.trim()) e.tenantSlug = t("login.errors.tenantRequired");
    if (!email.trim()) e.email = t("login.errors.emailRequired");
    else if (email.includes("@") && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = t("login.errors.emailInvalid");
    if (!password) e.password = t("login.errors.passwordRequired");
    else if (password.length < 8) e.password = t("login.errors.passwordMinLength");
    return e;
  };

  const handleLogin = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) {
      showToast(Object.values(errs)[0] ?? t("login.errors.invalidInput"));
      return;
    }
    setPending(true);
    try {
      const result = await auth.login({
        tenantSlug: tenantSlug.trim().toLowerCase(),
        email: email.trim().toLowerCase(),
        password,
      });
      // R-AUTH-5: 2FA-enabled account → switch to the code step.
      if (isTwoFactorChallenge(result)) {
        setPendingToken(result.pendingToken);
        setTwoFaStage(true);
        setErrors({});
        showToast(t("login.toast.passwordCorrectNeed2fa"));
        return;
      }
      finishLogin(result.roles);
    } catch (err: unknown) {
      let msg = t("login.errors.unknown");
      if (err instanceof ApiError) {
        msg = (err as { displayMessage?: string }).displayMessage ?? msg;
        // S-2/S-3 wave 2 — a 429 account_locked carries retryAfterSeconds; start a
        // persistent countdown + disable submit until the window elapses.
        const body = (err as { body?: { code?: string; retryAfterSeconds?: number } }).body;
        if (body?.code === "account_locked" && typeof body.retryAfterSeconds === "number") {
          // Seed nowTs fresh: the tick interval only runs while lockUntil != null, so
          // before the first lock nowTs is frozen at mount — without this the first
          // countdown render would be inflated by the seconds-since-mount until the
          // interval's first tick ~1s later.
          const nowMs = Date.now();
          setNowTs(nowMs);
          setLockUntil(nowMs + body.retryAfterSeconds * 1000);
        }
      } else if (err instanceof Error) {
        msg = err.message;
      }
      setErrors({ general: msg });
      showToast(msg);
    } finally {
      setPending(false);
    }
  };

  // R-AUTH-5 — step 2: exchange the pending token + code for a session.
  const handleTwoFactor = async (): Promise<void> => {
    if (twoFaCode.trim().length < 6) {
      setErrors({ general: t("login.errors.twoFactorIncomplete") });
      return;
    }
    setPending(true);
    try {
      // toEnDigits: the field accepts Persian/Arabic-Indic digit input
      // (e.g. a Persian keyboard) — normalize before sending to the API.
      const user = await auth.loginTwoFactor({ pendingToken, code: toEnDigits(twoFaCode.trim()) });
      finishLogin(user.roles);
    } catch (err: unknown) {
      let msg = t("login.errors.twoFactorInvalid");
      if (err instanceof ApiError) {
        msg = (err as { displayMessage?: string }).displayMessage ?? msg;
      } else if (err instanceof Error) {
        msg = err.message;
      }
      setErrors({ general: msg });
      showToast(msg);
    } finally {
      setPending(false);
    }
  };

  const cancelTwoFactor = (): void => {
    setTwoFaStage(false);
    setTwoFaCode("");
    setPendingToken("");
    setErrors({});
  };

  return (
    <main
      className="r5-login-shell"
      data-screen-label="01 Login"
      style={{
        minHeight: "100dvh",
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 1fr)",
        background: "var(--r5-bg)",
        color: "var(--r5-ink)",
      }}
    >
      {/* Brand panel — hidden by CSS at <=720px */}
      <BrandPanel showTestimonial />

      {/* Form panel */}
      <section
        className="r5-form-panel"
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          padding: "clamp(20px, 4vw, 48px) clamp(20px, 5vw, 72px)",
          minHeight: "100dvh",
          background: "var(--r5-bg)",
        }}
      >
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "clamp(20px, 4vh, 48px)",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <Wordmark />
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <PillButton onClick={() => showToast(t("login.toast.searchDisabled"))} ariaLabel={t("login.aria.search")}>
              <Icon d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm10 2-4.35-4.35" size={16} />
            </PillButton>
            {/* Theme toggle removed (D-2026-06-13): dark mode disabled for now. */}
            {/* Informational indicator, not another language picker. Keep its
                visible active code in the accessible name for every locale. */}
            <PillButton onClick={() => showToast(`${languageLabel}: ${language?.nativeName ?? locale}`)} ariaLabel={`${languageLabel} ${languageCode}`}>
              <span data-testid="login-active-language" dir="ltr" style={{ fontSize: 12, fontWeight: 600 }}>{languageCode}</span>
            </PillButton>
          </div>
        </header>

        <div className="r5-form-center" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div
            style={{
              width: "100%",
              maxWidth: 460,
              animation: "r5-card-in 700ms cubic-bezier(.22,1,.36,1) both",
            }}
          >
            <Eyebrow text={t("login.hero.eyebrowText")} subtext={t("login.hero.eyebrowSubtext")} />
            <h1
              style={{
                fontSize: "clamp(34px, 4.4vw, 52px)",
                lineHeight: 1.05,
                margin: "10px 0 14px",
                fontWeight: 700,
                letterSpacing: "-0.01em",
                color: "var(--r5-ink)",
              }}
            >
              {t("login.hero.title")}
            </h1>
            <p style={{ color: "var(--r5-muted)", margin: 0, fontSize: 15, lineHeight: 1.65 }}>
              <Trans t={t} i18nKey="login.hero.subtitle" components={{ b: <b style={{ color: "var(--r5-ink-2)" }} /> }} />
            </p>

            {/* R-AUTH-5 — 2FA code step (shown after a correct password when the
                account has 2FA). The R5 login shell + brand panel are
                unchanged; only the form body swaps to this focused view. */}
            {twoFaStage ? (
            <form
              onSubmit={(e) => { e.preventDefault(); void handleTwoFactor(); }}
              style={{ marginTop: 26, display: "grid", gap: 16 }}
              noValidate
            >
              <div style={{ fontSize: 13, color: "var(--r5-ink-2)", lineHeight: 1.8 }}>
                {t("login.twoFactor.description")}
              </div>
              <Field label={t("login.twoFactor.codeLabel")} required error={errors.general}>
                <TextInput
                  name="twofa-code"
                  type="text"
                  value={twoFaCode}
                  onChange={(e) => setTwoFaCode(e.target.value)}
                  placeholder={t("login.twoFactor.codePlaceholder")}
                  dir="ltr"
                  icon="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z"
                />
              </Field>
              <button
                type="submit"
                disabled={pending}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  padding: "14px 18px", borderRadius: 14, border: "none",
                  cursor: pending ? "default" : "pointer",
                  background: "var(--r5-sage-deep)", color: "#fff",
                  fontFamily: "inherit", fontSize: 15, fontWeight: 600,
                }}
              >
                {pending ? (<><Spinner /> {t("login.twoFactor.submitting")}</>) : (<><span>{t("login.twoFactor.submit")}</span><Icon d="M5 12h14M13 5l7 7-7 7" size={18} stroke={2} /></>)}
              </button>
              <button
                type="button"
                onClick={cancelTwoFactor}
                style={{ background: "none", border: "none", color: "var(--r5-sage-deep)", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
              >
                {t("login.twoFactor.back")}
              </button>
            </form>
            ) : (
            <form onSubmit={handleLogin} style={{ marginTop: 26, display: "grid", gap: 16 }} noValidate>
              {/* Demo quick-login by role — S-1: ONLY in a VITE_DEMO_MODE build
                  (tree-shaken out of production) AND on the demo tenant. Clicking a
                  chip fills that role's real seeded demo account; the actual
                  post-login role still comes from the account server-side. */}
              {DEMO_MODE && tenantSlug.trim().toLowerCase() === "demo" && (
                <DemoRolePicker selected={selectedRole} onPick={pickDemo} />
              )}

              {/* Tenant — hidden by default (پیش‌فرض «demo» ساکت اعمال می‌شود)؛
                  فقط برایِ ورودِ سازمانی/مستأجرِ غیرِ پیش‌فرض آشکار می‌شود. */}
              {showTenantField ? (
                <Field
                  label={t("login.tenant.label")}
                  required
                  hint={
                    <span style={{ fontSize: 11, color: "var(--r5-muted)", fontFamily: "var(--r5-mono)" }}>
                      {t("login.tenant.hint")}
                    </span>
                  }
                  error={errors.tenantSlug}
                >
                  <TextInput
                    name="tenantSlug"
                    type="text"
                    value={tenantSlug}
                    onChange={(e) => {
                      setTenantSlug(e.target.value);
                      setLockUntil(null); // switching account context clears a stale lock countdown
                    }}
                    placeholder="demo"
                    icon="M12 8v8M8 12h8M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2Z"
                  />
                </Field>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowTenantField(true)}
                  style={{
                    justifySelf: "start",
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: "var(--r5-muted)",
                    fontSize: 12,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    textDecoration: "underline",
                  }}
                >
                  {t("login.tenant.toggleShow")}
                </button>
              )}

              {/* Email */}
              <Field label={t("login.email.label")} required error={errors.email}>
                <TextInput
                  name="email"
                  type="text"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setLockUntil(null); // switching account clears a stale lock countdown
                  }}
                  placeholder={EMAIL_PLACEHOLDER}
                  dir="ltr"
                  icon="M22 6 12 13 2 6m20 0v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6m20 0a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2"
                />
              </Field>

              {/* Password */}
              <Field
                label={t("login.password.label")}
                required
                error={errors.password}
                hint={
                  <a
                    href="#forgot"
                    onClick={(e) => {
                      e.preventDefault();
                      go("forgot");
                    }}
                    style={{
                      fontSize: 12,
                      color: "var(--r5-sage-deep)",
                      textDecoration: "none",
                      fontWeight: 500,
                    }}
                  >
                    {t("login.password.forgotLink")}
                  </a>
                }
              >
                <TextInput
                  name="password"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••"
                  dir="ltr"
                  style={{ paddingInlineStart: 52, paddingInlineEnd: 44 }}
                  icon="M12 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm6-7V8a6 6 0 1 0-12 0v2H4v12h16V10h-2Z"
                />
                <button
                  type="button"
                  dir="ltr"
                  onClick={() => setShowPass((s) => !s)}
                  aria-label={showPass ? t("login.password.hideAria") : t("login.password.showAria")}
                  style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    insetInlineStart: 4,
                    width: 36,
                    background: "transparent",
                    border: 0,
                    color: "var(--r5-muted)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {showPass ? (
                    <Icon d="M3 3 21 21M10.6 6.1A10 10 0 0 1 12 6c5 0 9 6 9 6a14 14 0 0 1-3.2 3.6M6.6 6.6A14 14 0 0 0 3 12s4 6 9 6a10 10 0 0 0 4.4-1M9.5 9.5a3.5 3.5 0 0 0 5 5" />
                  ) : (
                    <Icon d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Zm10 3a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                  )}
                </button>
                <PassStrength value={password} />
              </Field>

              {/* Toggles row */}
              <div style={{ display: "grid", gap: 10, marginTop: 4 }}>
                <Checkbox checked={remember} onChange={setRemember} label={t("login.remember.label")} />
                <Checkbox
                  checked={twofa}
                  onChange={setTwofa}
                  label={t("login.twofa.label")}
                  hint={t("login.twofa.hint")}
                />
              </div>

              {/* S-2/S-3 wave 2 — persistent lockout countdown (toast vanishes). */}
              {isLocked && (
                <div
                  role="alert"
                  style={{
                    marginTop: 10,
                    padding: "10px 12px",
                    borderRadius: 12,
                    fontSize: 13,
                    lineHeight: 1.7,
                    color: "var(--r5-ink)",
                    background: "color-mix(in oklch, var(--r5-danger, oklch(0.62 0.19 25)) 12%, var(--r5-paper))",
                    border: "1px solid color-mix(in oklch, var(--r5-danger, oklch(0.62 0.19 25)) 35%, transparent)",
                  }}
                >
                  {t("login.lockout.message", { countdown: formatLockCountdown(lockRemaining) })}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={pending || isLocked}
                style={{
                  marginTop: 10,
                  background: "var(--r5-ink)",
                  color: "var(--r5-paper)",
                  border: 0,
                  borderRadius: 14,
                  padding: "15px 20px",
                  fontSize: 15.5,
                  fontWeight: 600,
                  opacity: isLocked ? 0.55 : 1,
                  cursor: pending ? "wait" : isLocked ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  position: "relative",
                  overflow: "hidden",
                  transition: "transform 160ms ease, box-shadow 200ms ease",
                  boxShadow: "0 8px 24px -8px color-mix(in oklch, var(--r5-ink) 70%, transparent)",
                  minHeight: 48,
                }}
                onMouseEnter={(e) => {
                  if (!pending) e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                }}
              >
                {pending ? (
                  <><Spinner /> {t("login.submit.pending")}</>
                ) : (
                  <>
                    <span>{t("login.submit.default")}</span>
                    <Icon d="M5 12h14M13 5l7 7-7 7" size={18} stroke={2} />
                  </>
                )}
              </button>

              {/* OR divider */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  margin: "8px 0 0",
                  fontSize: 11,
                  color: "var(--r5-muted)",
                  fontFamily: "var(--r5-mono)",
                  letterSpacing: "0.18em",
                }}
              >
                <span style={{ flex: 1, height: 1, background: "var(--r5-line-strong)" }} />
                <span>{t("login.divider.orText")}</span>
                <span style={{ flex: 1, height: 1, background: "var(--r5-line-strong)" }} />
              </div>

              {/* SSO */}
              <div className="r5-sso-row" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <SsoButton label={t("login.sso.google.label")} glyph={GoogleGlyph} onClick={() => showToast(t("login.sso.google.toast"))} />
                <SsoButton label={t("login.sso.national.label")} hint="ID.IR" glyph={NationalGlyph} onClick={() => showToast(t("login.sso.national.toast"))} />
                <SsoButton label={t("login.sso.org.label")} hint="SAML / SSO" glyph={OrgGlyph} onClick={() => showToast(t("login.sso.org.toast"))} />
              </div>

              {/* Sign-up CTA — R-AUTH-1: application-only, leads to /apply
                  (open self-registration retired). Appearance unchanged. */}
              <a
                href="/apply"
                onClick={(e) => {
                  e.preventDefault();
                  go("apply");
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 14px",
                  background: "color-mix(in oklch, var(--r5-sage) 14%, var(--r5-paper))",
                  border: "1px dashed color-mix(in oklch, var(--r5-sage) 40%, transparent)",
                  borderRadius: 12,
                  textDecoration: "none",
                  color: "var(--r5-ink-2)",
                  fontSize: 13.5,
                  marginTop: 4,
                  transition: "background 200ms ease",
                }}
              >
                <Icon d="M12 4v16M4 12h16" size={14} stroke={2} />
                <span>
                  <Trans t={t} i18nKey="login.signup.cta" components={{ b: <b style={{ color: "var(--r5-ink)" }} /> }} />
                </span>
              </a>
            </form>
            )}
          </div>
        </div>

        <CoBrandFooter />
      </section>

      {toastNode}
    </main>
  );
};

export default LoginPage;
